import React, { useEffect, useState } from 'react';
import { dienstApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import MagnetboardView from './MagnetboardView';

export default function Tagesuebersicht({ wacheId }) {
  const [data, setData] = useState(null);
  const [fahrzeuge, setFahrzeuge] = useState([]);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    let timer;
    let requestId = 0;
    setData(null);
    const load = async () => {
      const id = ++requestId;
      clearTimeout(timer);
      try {
        const [board, vehicles] = await Promise.all([
          dienstApi.getTagesuebersicht(), dienstApi.getFahrzeuge({ nur_aktive: true }),
        ]);
        if (!active || id !== requestId) return;
        setData(board.data);
        setFahrzeuge(vehicles.data);
        setError(false);
        // Serverzeit statt Geräte-Zeitzone. Spätestens minütlich neue Besetzungen laden.
        const untilChange = Date.parse(board.data.naechster_wechsel) - Date.parse(board.data.serverzeit);
        timer = setTimeout(load, Math.min(60000, Math.max(250, untilChange + 100)));
      } catch {
        if (!active || id !== requestId) return;
        setError(true);
        setData(null);
        timer = setTimeout(load, 15000);
      }
    };
    const wake = () => { if (document.visibilityState === 'visible') load(); };
    load();
    window.addEventListener('focus', wake);
    document.addEventListener('visibilitychange', wake);
    return () => {
      active = false;
      clearTimeout(timer);
      window.removeEventListener('focus', wake);
      document.removeEventListener('visibilitychange', wake);
    };
  }, [wacheId, retry]);

  return <div className="space-y-3" data-testid="wachen-tagesuebersicht">
    <div>
      <h2 className="text-xl font-semibold">Tagesübersicht der Wache</h2>
      <p className="text-sm text-muted-foreground">Alle Wachabteilungen</p>
    </div>
    {(data?.tag.hat_testdaten || data?.nacht.hat_testdaten) && <p className="rounded-lg bg-amber-100 p-3 text-sm text-amber-950">Lokale Testbesetzungen: Diese Übersicht enthält zufällig zugeteilte Mitarbeiter zum Ausprobieren.</p>}
    {error ? <div role="alert" className="rounded-lg border p-6">
      <p>Die Tagesübersicht konnte nicht aktualisiert werden.</p>
      <Button variant="outline" className="mt-3" onClick={() => setRetry(value => value + 1)}>Erneut laden</Button>
    </div> : !data ? <Skeleton className="h-96" /> : (
      <div className="overflow-x-auto">
        <div className="grid grid-cols-2 gap-4 min-w-[700px]">
          {['tag', 'nacht'].map(key => {
            const shift = data[key];
            return <section key={key} aria-label={key === 'tag' ? 'Tagdienst' : 'Nachtdienst'} className="min-w-0 space-y-2">
              <div className="rounded-md bg-background px-4 py-4">
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                  <h3 className="text-base font-semibold tracking-tight">{key === 'tag' ? 'Tagdienst' : 'Nachtdienst'}</h3>
                  <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                    <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${shift.ist_aktuell ? 'bg-emerald-600 dark:bg-emerald-400' : 'bg-slate-300 dark:bg-slate-600'}`} />
                    {shift.ist_aktuell ? 'Aktuell im Dienst' : 'Kommende Schicht'}
                  </span>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{shift.abteilungen.map(department => department.name).join(' · ') || 'Noch keine Wachabteilung zugeordnet'}</p>
              </div>
              <MagnetboardView boardData={shift} boardFahrzeuge={fahrzeuge} />
            </section>;
          })}
        </div>
      </div>
    )}
  </div>;
}
